import React from 'react';
import { useInView } from 'react-intersection-observer';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { FaEnvelope, FaPhone } from 'react-icons/fa';
import 'react-lazy-load-image-component/src/effects/blur.css';

const About = ({ about, years, language, contact }) => {
  const description = language === 'pl' ? about.description_pl : about.description_en;
  const replacedDescription = description.replace('{{years}}', years);

  const skills = [
    { name: 'HTML/CSS', level: 90 },
    { name: 'JavaScript', level: 85 },
    { name: 'React', level: 85 },
    { name: 'Node.js', level: 75 },
    { name: 'Python', level: 70 },
  ];

  const { ref: skillsRef, inView: skillsInView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section id="about" className="section relative">
      <div className="container mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-center gap-12 max-w-6xl mx-auto">
          {/* Profile Image Column */}
          <div className="w-full md:w-1/3 flex justify-center">
             <div className="relative">
                <div className="absolute inset-0 bg-[#04c7e1] rounded-full blur-2xl opacity-20 animate-pulse"></div>
                <LazyLoadImage
                  alt="Kamil Priefer"
                  src="/images/profile.jpg"
                  effect="blur"
                  className="relative w-48 h-48 md:w-64 md:h-64 rounded-full object-cover border-4 border-white/10 shadow-2xl"
                />
             </div>
          </div>

          {/* Content Column */}
          <div className="w-full md:w-2/3">
             <div className="glass-card p-8 md:p-12">
                <div className="mb-2">
                  <span className="text-[#04c7e1] font-extrabold tracking-[0.2em] text-sm uppercase">Priefer IT</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-black mb-4 leading-none">
                  TWÓJ <span className="text-[#f2852d]">INFORMATYK</span><br className="hidden md:block" /> POZNAŃ
                </h1>
                <h2 className="text-xl font-bold mb-6 text-[#04c7e1]/80 border-b-2 border-[#f2852d] inline-block pb-1">
                  {language === 'pl' ? 'O mnie' : 'About Me'}
                </h2>
                {contact && (
                  <div className="flex flex-wrap gap-4 mb-10">
                    <a 
                      href={`mailto:${contact.email}`} 
                      className="flex items-center gap-3 bg-[#04c7e1]/10 hover:bg-[#04c7e1]/20 px-5 py-3 rounded-xl border border-[#04c7e1]/20 transition-all group shadow-lg backdrop-blur-sm"
                    >
                      <FaEnvelope className="text-[#04c7e1] group-hover:scale-110 transition-transform text-xl" />
                      <span className="text-gray-200 font-semibold tracking-wide">{contact.email}</span>
                    </a>
                    {contact.phone && (
                      <a 
                        href={`tel:${contact.phone.replace(/\s/g, '')}`} 
                        className="flex items-center gap-3 bg-[#f2852d]/10 hover:bg-[#f2852d]/20 px-5 py-3 rounded-xl border border-[#f2852d]/20 transition-all group shadow-lg backdrop-blur-sm"
                      >
                        <FaPhone className="text-[#f2852d] group-hover:scale-110 transition-transform text-xl" />
                        <span className="text-gray-200 font-semibold tracking-wide">{contact.phone}</span>
                      </a>
                    )}
                  </div>
                )}

                <p className="text-gray-300 text-lg leading-relaxed mb-8 text-justify">
                  {replacedDescription}
                </p>

                <div className="grid grid-cols-1 gap-8" ref={skillsRef}>
                  <div>
                    <h3 className="text-xl font-semibold mb-4 text-white">
                      {language === 'pl' ? 'Kluczowe Umiejętności' : 'Key Skills'}
                    </h3>
                    <div className="space-y-4">
                      {skills.map((skill, index) => (
                        <div key={index}>
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-300 font-medium">{skill.name}</span>
                            <span className="text-gray-400 text-sm">{skill.level}%</span>
                          </div>
                          <div className="w-full bg-gray-700/50 rounded-full h-2.5 backdrop-blur-sm">
                            <div 
                              className="bg-gradient-to-r from-[#04c7e1] to-[#f2852d] h-2.5 rounded-full transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(4,199,225,0.5)]"
                              style={{ width: skillsInView ? `${skill.level}%` : '0%' }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
             </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
